import {
  ChatConversation,
  ChatMessage,
  OrganizationMember,
  Prisma,
} from '@prisma/client'
import { Api } from '~/core/trpc'

export type FindManyConversations = Prisma.ChatConversationGetPayload<{
  include: { participants: true; messages: true }
}>[]

export type FindOneConversation = Prisma.ChatConversationGetPayload<{
  include: { participants: true }
}>

type Props = { organizationMember: OrganizationMember }

export const useSdk = ({ organizationMember }: Props) => {
  const findOneConversationAction = Api.dataConnector.actionRun.useMutation()
  const createConversationAction = Api.dataConnector.actionRun.useMutation()
  const sendMessageAction = Api.dataConnector.actionRun.useMutation()
  const sendDraftAction = Api.dataConnector.actionRun.useMutation()
  const clearConversationAction = Api.dataConnector.actionRun.useMutation()
  const conversationSummaryClear =
    Api.conversationSummaryCustom.clear.useMutation()

  const body = {
    organizationMemberId: organizationMember.id,
    providerKey: 'uichatV2',
  }

  async function findOneConversation(args: {
    organizationMemberId: string
  }): Promise<FindOneConversation> {
    const conversations = await findManyConversations({
      organizationMemberId: organizationMember.id,
    })

    const conversation = conversations.find(candidate => {
      const participantFound = candidate.participants.find(
        participant =>
          participant.organizationMemberId === args.organizationMemberId,
      )
      const isDirectMessage = candidate.participants.length === 2
      return isDirectMessage && participantFound
    })

    return conversation
  }

  async function findManyConversations(args: {
    organizationMemberId: string
  }): Promise<FindManyConversations> {
    const { response } = await findOneConversationAction.mutateAsync({
      ...body,
      providerActionKey: 'findManyConversations',
      args: args,
    })

    return response.conversations
  }

  async function createConversation(args: { organizationMemberIds: string[] }) {
    const { response } = await createConversationAction.mutateAsync({
      ...body,
      providerActionKey: 'createConversation',
      args: args,
    })

    return response.conversation as ChatConversation
  }

  async function sendMessage(args: {
    conversationId: string
    parentId?: string
    idTemporary?: string
    content: string
    [key: string]: any
  }) {
    const { response } = await sendMessageAction.mutateAsync({
      ...body,
      providerActionKey: 'sendMessage',
      args: args,
    })

    return response.message as ChatMessage
  }

  async function sendDraft(args: { id: string; content: string }) {
    const { response } = await sendMessageAction.mutateAsync({
      ...body,
      providerActionKey: 'sendDraft',
      args: args,
    })

    return response.message as ChatMessage
  }

  async function addReaction(args: { messageId: string; reaction: string }) {
    await sendMessageAction.mutateAsync({
      ...body,
      providerActionKey: 'addReaction',
      args: args,
    })

    return
  }

  async function clearConversation(args: { conversationId: string }) {
    await clearConversationAction.mutateAsync({
      ...body,
      providerActionKey: 'clearConversation',
      args: args,
    })

    await conversationSummaryClear.mutateAsync({})

    return
  }

  return {
    findOneConversation,
    findManyConversations,
    createConversation,
    sendMessage,
    addReaction,
    sendDraft,
    clearConversation,
  }
}
